import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { cancelOrder, fetchOrder, payOrder } from '../api/endpoints';
import { useCountdown } from '../hooks/useCountdown';
import {
  closeApp,
  copyToClipboard,
  downloadFile,
  haptic,
  openExternalLink,
  platform,
  showAlert,
  showConfirm,
} from '../lib/tg';
import { formatUZS } from '../lib/format';
import TopBar from '../components/TopBar';
import Splash from '../components/Splash';

// Способ приёма оплаты. 'cards' (перевод на карту) выключен, но код не удалён —
// чтобы вернуть старый флоу, достаточно поменять значение.
const PAYMENT_METHOD: 'qr' | 'cards' = 'qr';

const QR_FILE = '/qr-pay.png';

// scheme открывает установленное приложение напрямую; если приложения нет,
// переход по scheme молча ничего не делает — тогда по таймеру открываем ios/android ссылку.
const PAY_APPS = [
  {
    key: 'click',
    name: 'Click',
    scheme: 'clickuz://',
    ios: 'https://my.click.uz',
    android: 'https://my.click.uz',
  },
  {
    key: 'paynet',
    name: 'Paynet',
    scheme: 'paynet://',
    ios: 'https://apps.apple.com/uz/app/paynet-uz/id1307888692',
    android: 'https://play.google.com/store/apps/details?id=uz.paynet.app',
  },
  {
    key: 'uzum',
    name: 'Uzum Bank',
    scheme: 'uzumbank://',
    ios: 'https://apps.apple.com/uz/app/uzum-bank-online/id1492307726',
    android: 'https://play.google.com/store/apps/details?id=uz.kapitalbank.android',
  },
];

export default function Payment() {
  const { t } = useTranslation();
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [copiedId, setCopiedId] = useState<number | string | null>(null);
  const [qrSaved, setQrSaved] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['order', orderId],
    queryFn: () => fetchOrder(orderId!),
    enabled: !!orderId,
    refetchInterval: 10_000,
  });

  const heldUntilIso = (() => {
    if (!data || data.status !== 'HELD') return null;
    if (data.held_until) return data.held_until;
    if (data.hold_ttl) return new Date(Date.now() + data.hold_ttl * 1000).toISOString();
    return null;
  })();
  const { mmss, expired } = useCountdown(heldUntilIso);

  const cancelMut = useMutation({
    mutationFn: () => cancelOrder(Number(orderId)),
    onSuccess: async () => {
      haptic('light');
      await showAlert('Бронь отменена');
      navigate('/', { replace: true });
    },
    onError: async (err: any) => {
      const detail = err?.response?.data?.detail;
      await showAlert(typeof detail === 'string' ? detail : 'Не удалось отменить');
    },
  });

  async function onCancelClick() {
    const ok = await showConfirm('Отменить бронь и освободить места?');
    if (ok) cancelMut.mutate();
  }

  const payMut = useMutation({
    mutationFn: () => payOrder(Number(orderId)),
    onSuccess: async () => {
      haptic('heavy');
      await showAlert(
        'Откройте чат с ботом и пришлите фото-скриншот вашего перевода. ' +
          'Администратор подтвердит оплату, и билеты придут в этот же чат.',
      );
      setTimeout(() => closeApp(), 150);
    },
  });

  const instructionText =
    PAYMENT_METHOD === 'qr'
      ? t('payment.qrInstruction', { amount: data ? formatUZS(data.total_amount) : '' })
      : t('payment.instruction');

  async function onPayClick() {
    const ok = await showConfirm(instructionText);
    if (!ok) return;
    payMut.mutate();
  }

  async function onDownloadQr() {
    haptic('light');
    const url = `${window.location.origin}${QR_FILE}`;
    const accepted = await downloadFile(url, 'oplata-qr.png');
    if (accepted) {
      setQrSaved(true);
    } else {
      // Старый клиент Telegram без downloadFile (Bot API < 8.0):
      // откроем картинку в браузере, сохранить можно лонг-тапом.
      openExternalLink(url);
    }
  }

  function copyAmount(amount: number) {
    copyToClipboard(String(amount));
    setCopiedId('amount');
    setTimeout(() => setCopiedId((curr) => (curr === 'amount' ? null : curr)), 1500);
  }

  function openPayApp(app: (typeof PAY_APPS)[number]) {
    haptic('light');
    if (platform() === 'ios') {
      // Telegram iOS блокирует кастомные схемы внутри вебвью — открываем
      // трамплин /open.html в Safari, оттуда схема срабатывает.
      // /open без .html — Cloudflare Pages редиректит .html на pretty URL
      openExternalLink(`${window.location.origin}/open?app=${app.key}`);
      return;
    }
    const fallback = app.android;
    // Если приложение установлено, переход по scheme уведёт из мини-аппа и
    // webview уйдёт в фон — тогда фолбэк отменяем. Если схема никем не
    // зарегистрирована, ничего не произойдёт и сработает таймер.
    const timer = setTimeout(() => {
      document.removeEventListener('visibilitychange', onHide);
      openExternalLink(fallback);
    }, 1500);
    const onHide = () => {
      if (document.visibilityState === 'hidden') {
        clearTimeout(timer);
        document.removeEventListener('visibilitychange', onHide);
      }
    };
    document.addEventListener('visibilitychange', onHide);
    window.location.href = app.scheme;
  }

  function copyCard(card: string, id: number | string) {
    copyToClipboard(card.replace(/\s+/g, ''));
    setCopiedId(id);
    setTimeout(() => setCopiedId((curr) => (curr === id ? null : curr)), 1500);
  }

  function maskCard(card: string): string {
    const digits = card.replace(/\D/g, '');
    if (digits.length < 8) return card;
    const first = digits.slice(0, 4);
    const last = digits.slice(-4);
    return `${first} •••• •••• ${last}`;
  }

  if (isLoading || !data) return <Splash />;

  const pending = data.status === 'PAID_PENDING';

  return (
    <div className="min-h-screen pb-32">
      <TopBar title={t('payment.title')} />

      <div className="px-4 pt-3">
        {pending && (
          <div className="card p-4 mb-3 bg-warn/10 border border-warn/30">
            <div className="font-semibold text-warn">{t('payment.waitingAdmin')}</div>
          </div>
        )}

        {!pending && !expired && data.status === 'HELD' && (
          <div className="text-center text-sm text-muted mb-3">
            {t('seats.timeLeft', { time: mmss })}
          </div>
        )}

        <div className="card p-4 mb-3">
          <div className="text-sm text-muted mb-1">{data.session_title}</div>
          <div className="text-xs text-muted">{data.cinema_name}</div>
        </div>

        <div className="card p-4 mb-3">
          <div className="text-xs text-muted mb-2">{t('payment.yourSeats')}</div>
          {data.seats.map((s) => (
            <div key={s.seat_id} className="flex items-center justify-between py-1.5 border-b border-line/60 last:border-0">
              <div className="text-sm">
                <span className="text-muted">{s.sector_name}</span>{' · '}
                {t('seats.row')} {s.row_num}, {t('seats.seat')} {s.seat_num}
              </div>
              <div className="text-sm font-medium">{formatUZS(s.price)}</div>
            </div>
          ))}
          <div className="flex items-center justify-between pt-3 mt-1 border-t border-line">
            <div className="font-semibold">{t('payment.total')}</div>
            <div className="font-bold text-accent-soft">{formatUZS(data.total_amount)} {t('common.sum')}</div>
          </div>
        </div>

        {PAYMENT_METHOD === 'qr' && (
          <>
            <div className="card p-4 mb-3">
              <div className="text-xs text-muted mb-2">{t('payment.qrLabel')}</div>
              <div className="bg-white rounded-xl p-3 mx-auto w-56">
                <img src={QR_FILE} alt="QR" className="w-full" />
              </div>
              <button className="btn-secondary w-full mt-3" onClick={onDownloadQr}>
                {qrSaved ? t('payment.qrSaved') : t('payment.downloadQr')}
              </button>
              <button
                className="btn-secondary w-full mt-2"
                onClick={() => copyAmount(data.total_amount)}
              >
                {copiedId === 'amount'
                  ? t('payment.amountCopied')
                  : `${t('payment.copyAmount')} — ${formatUZS(data.total_amount)}`}
              </button>
            </div>

            <div className="card p-4 mb-3">
              <div className="text-xs text-muted mb-2">{t('payment.openApp')}</div>
              <div className="grid grid-cols-3 gap-2">
                {PAY_APPS.map((app) => (
                  <button
                    key={app.key}
                    className="btn-secondary text-sm px-1"
                    onClick={() => openPayApp(app)}
                  >
                    {app.name}
                  </button>
                ))}
              </div>
            </div>
          </>
        )}

        {PAYMENT_METHOD === 'cards' && (() => {
          const cards =
            data.payment_cards && data.payment_cards.length > 0
              ? data.payment_cards.map((c, i) => ({ key: `c${i}`, number: c.number, holder: c.holder || '' }))
              : data.card_number
              ? [{ key: 'legacy', number: data.card_number, holder: data.card_holder || '' }]
              : [];
          if (cards.length === 0) {
            return (
              <div className="card w-full p-4 mb-3 text-center text-xs text-muted">
                Карта не настроена
              </div>
            );
          }
          return (
            <div className="space-y-2 mb-3">
              {cards.map((c) => {
                const isCopied = copiedId === c.key;
                return (
                  <button
                    key={c.key}
                    onClick={() => copyCard(c.number, c.key)}
                    className="card w-full p-4 text-left active:bg-bg-elev"
                  >
                    <div className="text-xs text-muted mb-1">{t('payment.cardLabel')}</div>
                    <div className="text-lg font-mono tracking-widest">{maskCard(c.number)}</div>
                    {c.holder && <div className="text-xs text-muted mt-1">{c.holder}</div>}
                    <div className="text-xs mt-1.5" style={{ color: isCopied ? '#22c55e' : '#8a82a8' }}>
                      {isCopied ? t('payment.copied') : t('payment.tapToCopy')}
                    </div>
                  </button>
                );
              })}
            </div>
          );
        })()}

        {!pending && (
          <p className="text-sm text-muted leading-relaxed mb-4 px-1">
            {instructionText}
          </p>
        )}
      </div>

      {!pending && (
        <div className="fixed bottom-0 inset-x-0 p-4 bg-bg border-t border-line space-y-2">
          <button
            className="btn-primary"
            onClick={onPayClick}
            disabled={payMut.isPending || cancelMut.isPending || expired}
          >
            {t('payment.iPaid')}
          </button>
          <button
            className="btn-secondary w-full"
            onClick={onCancelClick}
            disabled={cancelMut.isPending || payMut.isPending}
          >
            {cancelMut.isPending ? '…' : 'Отменить бронь'}
          </button>
        </div>
      )}
    </div>
  );
}
